import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import useUIPreferenceStore from "../../stores/useUIPreferenceStore";
import { ContextAgentContext } from "./ContextAgentContext";
import ContextAgentPanelHost from "./ContextAgentPanelHost";
import ContextAgentSidebarTab from "./ContextAgentSidebarTab";
import ContextAgentWidgetLauncher from "./ContextAgentWidgetLauncher";
import {
  clampWidgetAnchor,
  clampWidgetPanelAnchor,
  getDefaultWidgetAnchor,
  getWidgetPanelAnchorFromSprite,
} from "./widgetAnchor";

function ContextAgentMainColumn({ children }) {
  return (
    <Box
      component="main"
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      <ContextAgentSidebarTab />
      <ContextAgentWidgetLauncher />
      <ContextAgentPanelHost />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function ContextAgentProvider({ sidebar, children }) {
  const assistantLayout = useUIPreferenceStore((s) => s.assistantLayout);
  const [registration, setRegistrationState] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [widgetAnchor, setWidgetAnchorState] = useState(getDefaultWidgetAnchor);
  const [widgetPanelAnchor, setWidgetPanelAnchorState] = useState(null);
  const widgetPanelDragRef = useRef(null);
  const registrationRef = useRef(null);
  registrationRef.current = registration;

  const isControlled =
    registration?.open !== undefined &&
    typeof registration?.onOpenChange === "function";

  const open = isControlled ? registration.open : internalOpen;

  const setOpen = useCallback((next) => {
    const reg = registrationRef.current;
    if (reg?.onOpenChange) {
      const current = reg.open ?? false;
      reg.onOpenChange(typeof next === "function" ? next(current) : next);
      return;
    }
    setInternalOpen((prev) => (typeof next === "function" ? next(prev) : next));
  }, []);

  const toggleOpen = useCallback(() => {
    const reg = registrationRef.current;
    if (reg?.onToggleClick) {
      reg.onToggleClick({ open: reg.open ?? internalOpen, setOpen });
      return;
    }
    setOpen((prev) => !prev);
  }, [setOpen, internalOpen]);

  const setWidgetAnchor = useCallback((next) => {
    setWidgetAnchorState((prev) => {
      const candidate = typeof next === "function" ? next(prev) : next;
      return clampWidgetAnchor(candidate);
    });
  }, []);

  const setWidgetPanelAnchor = useCallback((next) => {
    setWidgetPanelAnchorState((prev) => {
      const candidate = typeof next === "function" ? next(prev) : next;
      if (!candidate) {
        return null;
      }
      return clampWidgetPanelAnchor(candidate);
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setWidgetPanelAnchorState(null);
      return;
    }

    setWidgetPanelAnchorState((prev) =>
      prev ?? getWidgetPanelAnchorFromSprite(widgetAnchor)
    );
  }, [open, widgetAnchor]);

  useEffect(() => {
    const handleResize = () => {
      setWidgetAnchorState((prev) => clampWidgetAnchor(prev));
      setWidgetPanelAnchorState((prev) => (prev ? clampWidgetPanelAnchor(prev) : prev));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setRegistration = useCallback((next) => {
    if (!next) {
      setRegistrationState(null);
      setInternalOpen(false);
      return;
    }

    setRegistrationState((prev) => {
      if (
        prev &&
        prev.agentName === next.agentName &&
        prev.scopeLabel === next.scopeLabel &&
        prev.scopeId === next.scopeId &&
        prev.launcherAgentId === next.launcherAgentId &&
        prev.attentionMessage === next.attentionMessage &&
        prev.panel === next.panel &&
        prev.open === next.open &&
        prev.onOpenChange === next.onOpenChange &&
        prev.onToggleClick === next.onToggleClick
      ) {
        return prev;
      }

      return {
        agentName: next.agentName,
        scopeLabel: next.scopeLabel,
        scopeId: next.scopeId,
        launcherAgentId: next.launcherAgentId,
        attentionMessage: next.attentionMessage,
        panel: next.panel ?? null,
        open: next.open,
        onOpenChange: next.onOpenChange ?? null,
        onToggleClick: next.onToggleClick ?? null,
      };
    });
  }, []);

  const clearRegistration = useCallback(() => {
    setRegistration(null);
  }, [setRegistration]);

  const value = useMemo(
    () => ({
      registration,
      open,
      setOpen,
      toggleOpen,
      setRegistration,
      clearRegistration,
      isRegistered: Boolean(registration),
      assistantLayout,
      widgetPanelDragRef,
      widgetAnchor,
      setWidgetAnchor,
      widgetPanelAnchor,
      setWidgetPanelAnchor,
    }),
    [
      registration,
      open,
      setOpen,
      toggleOpen,
      setRegistration,
      clearRegistration,
      assistantLayout,
      widgetAnchor,
      setWidgetAnchor,
      widgetPanelAnchor,
      setWidgetPanelAnchor,
    ]
  );

  if (sidebar) {
    return (
      <ContextAgentContext.Provider value={value}>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {sidebar}
          <ContextAgentMainColumn>{children}</ContextAgentMainColumn>
        </Box>
      </ContextAgentContext.Provider>
    );
  }

  return (
    <ContextAgentContext.Provider value={value}>
      <ContextAgentMainColumn>{children}</ContextAgentMainColumn>
    </ContextAgentContext.Provider>
  );
}
